/**
 * @krisspy-file
 * @type page
 * @name "DiscountCodeForm"
 * @title "Discount Code Form"
 * @description "Create or edit a discount code"
 * @routes ["/discount-codes/create", "/discount-codes/:id/edit"]
 * @flowName "discount-management"
 * @layout "DashboardLayout"
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Save,
  X,
  Loader2,
  AlertCircle,
  Trash2,
  Calendar,
  Clock,
  Percent,
} from 'lucide-react';
import {
  getDiscountCodeById,
  createDiscountCode,
  updateDiscountCode,
  deleteDiscountCode,
  type DiscountCodeInput
} from '../services/discountCodes.service';
import { getProducts } from '../services/products.service';
import { getMerchantById } from '../services/merchants.service';
import { utcIsoToZonedYmd, ymdInZoneToUtcStartIso, ymdInZoneToUtcEndIso } from '../lib/timezone';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useDateFormatter } from '../hooks/useDateFormatter';
import NumberInput from '../components/NumberInput';

// ISO 8601 weekday numbering (1 = Monday … 7 = Sunday), the convention of
// discount_codes.valid_days_of_week. Deliberately different from
// pricing_rules.valid_days_of_week (0 = Sunday … 6 = Saturday) — never copy
// values between the two tables.
const ISO_DAYS_OF_WEEK: Array<{ value: number; labelKey: string }> = [
  { value: 1, labelKey: 'discountCodes.dayMon' },
  { value: 2, labelKey: 'discountCodes.dayTue' },
  { value: 3, labelKey: 'discountCodes.dayWed' },
  { value: 4, labelKey: 'discountCodes.dayThu' },
  { value: 5, labelKey: 'discountCodes.dayFri' },
  { value: 6, labelKey: 'discountCodes.daySat' },
  { value: 7, labelKey: 'discountCodes.daySun' }
];

export default function DiscountCodeForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const { merchantId, locationIds, isPlatformAdmin } = useAuth();
  const { showToast } = useToast();
  const { formatDate, formatTime } = useDateFormatter();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage' as 'percentage' | 'fixed_amount' | 'timecard',
    discount_value: 0,
    max_value: null as number | null,
    valid_from: '',
    valid_to: '',
    validity_basis: 'booking_time' as 'booking_time' | 'rental_start',
    valid_time_from: '',
    valid_time_until: '',
    valid_days_of_week: [] as number[],
    max_uses: null as number | null,
    minimum_order_amount: null as number | null,
    applicable_product_types: [] as string[],
    active: true
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [products, setProducts] = useState<Array<{ id: string; product_name: string }>>([]);
  const [currency, setCurrency] = useState<string>('EUR');
  // IANA timezone of the code's merchant — validity dates are entered as
  // calendar days in this timezone and stored as real UTC instants
  // (start-of-day / end-of-day 23:59:59.999). Shared convention with the
  // booking engine; see check_discount_temporal_validity (migration 139).
  const [merchantTimezone, setMerchantTimezone] = useState<string>('UTC');
  const [usageStats, setUsageStats] = useState({
    current_uses: 0,
    current_value_used: 0,
    created_at: '',
    updated_at: ''
  });

  useEffect(() => {
    loadFormData();
  }, [id]);

  async function loadFormData() {
    setLoading(true);

    // Load the existing code first (edit mode) so merchant settings —
    // currency and the timezone used for validity-date conversion — come
    // from the code's own merchant, which can differ from the logged-in
    // user's merchant for platform admins.
    let code = null;
    if (isEditMode && id) {
      const { data, error } = await getDiscountCodeById(id);

      if (error) {
        showToast('Failed to load discount code: ' + error, 'error');
        navigate('/discount-codes');
        return;
      }
      code = data;
    }

    // Load merchant currency and timezone
    const settingsMerchantId = code?.merchant_id || merchantId;
    let timezone = 'UTC';
    if (settingsMerchantId) {
      const { data: merchant } = await getMerchantById(settingsMerchantId);
      if (merchant?.currency_code) {
        setCurrency(merchant.currency_code);
      }
      timezone = merchant?.timezone || 'UTC';
      setMerchantTimezone(timezone);
    }

    // Load products for the merchant
    const { data: productsData } = await getProducts({ merchantId, locationIds, isPlatformAdmin });
    if (productsData) {
      setProducts(productsData);
    }

    if (code) {
      setFormData({
        code: code.code,
        description: code.description || '',
        discount_type: (code.discount_type as 'percentage' | 'fixed_amount' | 'timecard') || 'percentage',
        discount_value: code.discount_value,
        max_value: code.max_value,
        valid_from: code.valid_from ? utcIsoToZonedYmd(code.valid_from, timezone) : '',
        valid_to: code.valid_to ? utcIsoToZonedYmd(code.valid_to, timezone) : '',
        validity_basis: code.validity_basis === 'rental_start' ? 'rental_start' : 'booking_time',
        // TIME columns come back as 'HH:MM:SS'; <input type="time"> wants 'HH:MM'
        valid_time_from: code.valid_time_from ? code.valid_time_from.slice(0, 5) : '',
        valid_time_until: code.valid_time_until ? code.valid_time_until.slice(0, 5) : '',
        valid_days_of_week: code.valid_days_of_week || [],
        max_uses: code.max_uses,
        minimum_order_amount: code.minimum_order_amount,
        applicable_product_types: code.applicable_product_types || [],
        active: code.active ?? true
      });

      setUsageStats({
        current_uses: code.current_uses ?? 0,
        current_value_used: code.current_value_used ?? 0,
        created_at: code.created_at ?? '',
        updated_at: code.updated_at ?? ''
      });
    }

    setLoading(false);
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleDayOfWeekToggle = (day: number) => {
    setFormData((prev) => {
      const current = prev.valid_days_of_week;
      const newList = current.includes(day)
        ? current.filter(d => d !== day)
        : [...current, day].sort((a, b) => a - b);
      return { ...prev, valid_days_of_week: newList };
    });
  };

  const handleProductToggle = (productId: string) => {
    setFormData((prev) => {
      const current = prev.applicable_product_types;
      const newList = current.includes(productId)
        ? current.filter(id => id !== productId)
        : [...current, productId];
      return { ...prev, applicable_product_types: newList };
    });
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.code.trim()) {
      newErrors.code = t('discountCodes.codeRequired');
    }

    if (formData.discount_value <= 0) {
      newErrors.discount_value = t('discountCodes.valueGreaterThanZero');
    }

    if (formData.discount_type === 'percentage' && formData.discount_value > 100) {
      newErrors.discount_value = t('discountCodes.percentageMax');
    }

    if (formData.max_value !== null && formData.max_value <= 0) {
      newErrors.max_value = t('discountCodes.maxValuePositive');
    }

    if (formData.valid_from && formData.valid_to) {
      if (new Date(formData.valid_from) > new Date(formData.valid_to)) {
        newErrors.valid_to = t('discountCodes.endAfterStart');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !merchantId) return;

    setLoading(true);

    const codeData: DiscountCodeInput = {
      merchant_id: merchantId,
      code: formData.code,
      description: formData.description || null,
      discount_type: formData.discount_type,
      discount_value: formData.discount_value,
      max_value: formData.max_value ?? undefined,
      // Store real boundary instants in the merchant's timezone: start of the
      // first day, end (23:59:59.999) of the last day. Both systems compare
      // inclusively against these stored values — no reader-side end-of-day
      // reconstruction (shared convention with the booking engine).
      valid_from: formData.valid_from ? ymdInZoneToUtcStartIso(formData.valid_from, merchantTimezone) || null : null,
      valid_to: formData.valid_to ? ymdInZoneToUtcEndIso(formData.valid_to, merchantTimezone) || null : null,
      validity_basis: formData.validity_basis,
      valid_time_from: formData.valid_time_from || null,
      valid_time_until: formData.valid_time_until || null,
      valid_days_of_week: formData.valid_days_of_week.length > 0 ? formData.valid_days_of_week : null,
      max_uses: formData.max_uses,
      minimum_order_amount: formData.minimum_order_amount,
      applicable_product_types: formData.applicable_product_types.length > 0 ? formData.applicable_product_types : null,
      active: formData.active
    };

    if (isEditMode && id) {
      const { error } = await updateDiscountCode(id, codeData);
      if (error) {
        if (error.toLowerCase().includes('already exists')) {
          setErrors(prev => ({ ...prev, code: error }));
        } else {
          showToast('Failed to update discount code: ' + error, 'error');
        }
        setLoading(false);
        return;
      }
      showToast(t('discountCodes.updatedSuccess'), 'success');
    } else {
      const { error } = await createDiscountCode(codeData);
      if (error) {
        if (error.toLowerCase().includes('already exists')) {
          setErrors(prev => ({ ...prev, code: error }));
        } else {
          showToast('Failed to create discount code: ' + error, 'error');
        }
        setLoading(false);
        return;
      }
      showToast(t('discountCodes.createdSuccess'), 'success');
    }

    navigate('/discount-codes');
  };

  const handleDelete = async () => {
    if (!id) return;

    if (!confirm(t('discountCodes.deleteConfirm'))) {
      return;
    }

    setLoading(true);

    const { error } = await deleteDiscountCode(id);

    if (error) {
      showToast('Failed to delete discount code: ' + error, 'error');
      setLoading(false);
      return;
    }

    showToast(t('discountCodes.deletedSuccess'), 'success');
    navigate('/discount-codes');
  };

  const handleCancel = () => {
    navigate('/discount-codes');
  };

  if (loading && isEditMode) {
    return (
      <div className="space-y-2xl animate-fade-in">
        <div className="card">
          <div className="flex items-center justify-center py-2xl">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <span className="ml-md text-secondary">{t('discountCodes.loadingCode')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2xl animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-md">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-sm">
            {isEditMode ? t('discountCodes.editCode') : t('discountCodes.createCodeTitle')}
          </h1>
          <p className="text-sm sm:text-base text-secondary">
            {isEditMode ? t('discountCodes.updateDesc') : t('discountCodes.createDesc')}
          </p>
        </div>
        <div className="flex items-center gap-sm sm:gap-md flex-shrink-0">
          {isEditMode && (
            <button onClick={handleDelete} className="btn btn-ghost text-red-600 hover:bg-red-50">
              <Trash2 className="w-4 h-4" />
              <span className="hidden sm:inline">{t('common.delete')}</span>
            </button>
          )}
          <button onClick={handleCancel} className="btn btn-ghost text-sm" disabled={loading}>
            <X className="w-4 h-4" />
            <span className="hidden sm:inline">{t('common.cancel')}</span>
          </button>
          <button onClick={handleSave} className="btn btn-primary text-sm" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="hidden sm:inline">{t('common.saving')}</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span className="hidden sm:inline">{isEditMode ? t('common.update') : t('common.save')}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Form Content */}
      <div className="card">
        <div className="space-y-2xl">
          {/* Section 1: General */}
          <div className="space-y-lg">
            <h3 className="text-lg font-semibold text-primary">{t('discountCodes.generalInformation')}</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-primary mb-sm">
                  {t('discountCodes.code')} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="e.g., SUMMER2024"
                  className={`input w-full uppercase ${errors.code ? 'border-danger' : ''}`}
                  style={{ textTransform: 'uppercase' }}
                />
                {errors.code && (
                  <p className="text-danger text-xs mt-xs flex items-center gap-xs">
                    <AlertCircle className="w-3 h-3" />
                    {errors.code}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="active" className="block text-sm font-medium text-primary mb-sm">
                  {t('discountCodes.status')}
                </label>
                <select
                  id="active"
                  name="active"
                  value={formData.active ? 'true' : 'false'}
                  onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.value === 'true' }))}
                  className="input w-full"
                >
                  <option value="true">{t('common.active')}</option>
                  <option value="false">{t('common.inactive')}</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-primary mb-sm">
                {t('discountCodes.descriptionLabel')}
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder={t('discountCodes.descriptionPlaceholder')}
                rows={2}
                className="input w-full resize-none"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-lg">
              <div>
                <label htmlFor="discount_type" className="block text-sm font-medium text-primary mb-sm">
                  {t('discountCodes.discountType')} <span className="text-danger">*</span>
                </label>
                <select
                  id="discount_type"
                  name="discount_type"
                  value={formData.discount_type}
                  onChange={handleInputChange}
                  className="input w-full"
                >
                  <option value="percentage">{t('discountCodes.percentage')}</option>
                  <option value="fixed_amount">{t('discountCodes.fixedAmount')}</option>
                  <option value="timecard">{t('discountCodes.timecardPrepaid')}</option>
                </select>
              </div>

              <div>
                <label htmlFor="discount_value" className="block text-sm font-medium text-primary mb-sm">
                  {formData.discount_type === 'percentage' ? t('discountCodes.percentage') :
                   formData.discount_type === 'timecard' ? t('discountCodes.totalCredit') : t('discountCodes.amount')} <span className="text-danger">*</span>
                </label>
                {formData.discount_type === 'percentage' ? (
                  <div className="relative">
                    <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                    <NumberInput
                      value={formData.discount_value ?? 0}
                      onChange={(v) => setFormData(prev => ({ ...prev, discount_value: v }))}
                      mode="decimal"
                      min={0}
                      max={100}
                      step="0.01"
                      className={`input w-full pr-10 ${errors.discount_value ? 'border-danger' : ''}`}
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-sm">
                    <span className="text-sm font-medium text-secondary">{currency}</span>
                    <NumberInput
                      value={formData.discount_value ?? 0}
                      onChange={(v) => setFormData(prev => ({ ...prev, discount_value: v }))}
                      mode="decimal"
                      min={0}
                      step="0.01"
                      className={`input flex-1 ${errors.discount_value ? 'border-danger' : ''}`}
                    />
                  </div>
                )}
                {errors.discount_value && (
                  <p className="text-danger text-xs mt-xs flex items-center gap-xs">
                    <AlertCircle className="w-3 h-3" />
                    {errors.discount_value}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="max_value" className="block text-sm font-medium text-primary mb-sm">
                  {formData.discount_type === 'timecard' ? t('discountCodes.maxPerBooking') : t('discountCodes.maxDiscountCap')}
                </label>
                <div className="flex items-center gap-sm">
                  <span className="text-sm font-medium text-secondary">{currency}</span>
                  <NumberInput
                    value={formData.max_value ?? 0}
                    onChange={(v) => setFormData(prev => ({ ...prev, max_value: v || null }))}
                    mode="decimal"
                    min={0}
                    step="0.01"
                    placeholder="Optional"
                    className={`input flex-1 ${errors.max_value ? 'border-danger' : ''}`}
                  />
                </div>
                <p className="text-xs text-secondary mt-xs">
                  {formData.discount_type === 'timecard'
                    ? t('discountCodes.maxValueTimecardHint')
                    : formData.discount_type === 'percentage'
                    ? t('discountCodes.maxValuePercentageHint')
                    : t('discountCodes.maxValueFixedHint')}
                </p>
                {errors.max_value && (
                  <p className="text-danger text-xs mt-xs flex items-center gap-xs">
                    <AlertCircle className="w-3 h-3" />
                    {errors.max_value}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Section 2: Validity & Applicability */}
          <div className="border-t border-primary pt-lg space-y-lg">
            <h3 className="text-lg font-semibold text-primary">{t('discountCodes.validityApplicability')}</h3>

            <div>
              <label className="block text-sm font-medium text-primary mb-sm">
                {t('discountCodes.validityBasis')}
              </label>
              <div className="space-y-xs">
                <label className="flex items-start gap-sm cursor-pointer">
                  <input
                    type="radio"
                    name="validity_basis"
                    value="booking_time"
                    checked={formData.validity_basis === 'booking_time'}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                  <span className="text-sm text-primary">{t('discountCodes.validityBasisBookingTime')}</span>
                </label>
                <label className="flex items-start gap-sm cursor-pointer">
                  <input
                    type="radio"
                    name="validity_basis"
                    value="rental_start"
                    checked={formData.validity_basis === 'rental_start'}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                  <span className="text-sm text-primary">{t('discountCodes.validityBasisRentalStart')}</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
              <div>
                <label htmlFor="valid_from" className="block text-sm font-medium text-primary mb-sm">
                  {t('discountCodes.validFrom')}
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                  <input
                    type="date"
                    id="valid_from"
                    name="valid_from"
                    value={formData.valid_from}
                    onChange={handleInputChange}
                    className="input w-full pl-10"
                  />
                </div>
                <p className="text-xs text-secondary mt-xs">{t('discountCodes.noStartRestriction')}</p>
              </div>

              <div>
                <label htmlFor="valid_to" className="block text-sm font-medium text-primary mb-sm">
                  {t('discountCodes.validTo')}
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                  <input
                    type="date"
                    id="valid_to"
                    name="valid_to"
                    value={formData.valid_to}
                    onChange={handleInputChange}
                    className={`input w-full pl-10 ${errors.valid_to ? 'border-danger' : ''}`}
                  />
                </div>
                <p className="text-xs text-secondary mt-xs">{t('discountCodes.noExpiryHint')}</p>
                {errors.valid_to && (
                  <p className="text-danger text-xs mt-xs flex items-center gap-xs">
                    <AlertCircle className="w-3 h-3" />
                    {errors.valid_to}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-sm">
                {t('discountCodes.dailyTimeWindow')}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
                <div>
                  <label htmlFor="valid_time_from" className="block text-sm text-secondary mb-sm">
                    {t('discountCodes.validTimeFrom')}
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                    <input
                      type="time"
                      id="valid_time_from"
                      name="valid_time_from"
                      value={formData.valid_time_from}
                      onChange={handleInputChange}
                      className="input w-full pl-10"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="valid_time_until" className="block text-sm text-secondary mb-sm">
                    {t('discountCodes.validTimeUntil')}
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                    <input
                      type="time"
                      id="valid_time_until"
                      name="valid_time_until"
                      value={formData.valid_time_until}
                      onChange={handleInputChange}
                      className="input w-full pl-10"
                    />
                  </div>
                </div>
              </div>
              <p className="text-xs text-secondary mt-xs">{t('discountCodes.timeWindowHint')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-sm">
                {t('discountCodes.validDaysOfWeek')}
              </label>
              <div className="flex flex-wrap gap-sm">
                {ISO_DAYS_OF_WEEK.map(day => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => handleDayOfWeekToggle(day.value)}
                    className={`px-md py-sm text-sm rounded-md border transition-colors ${
                      formData.valid_days_of_week.includes(day.value)
                        ? 'bg-blue text-white border-blue'
                        : 'bg-tertiary text-secondary border-primary hover:bg-secondary'
                    }`}
                  >
                    {t(day.labelKey)}
                  </button>
                ))}
              </div>
              <p className="text-xs text-secondary mt-xs">{t('discountCodes.allDaysHint')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
              <div>
                <label htmlFor="max_uses" className="block text-sm font-medium text-primary mb-sm">
                  {t('discountCodes.maximumUses')}
                </label>
                <NumberInput
                  value={formData.max_uses ?? 0}
                  onChange={(v) => setFormData(prev => ({ ...prev, max_uses: v || null }))}
                  mode="int"
                  min={0}
                  placeholder="Unlimited"
                  className="input w-full"
                />
                <p className="text-xs text-secondary mt-xs">{t('discountCodes.unlimitedHint')}</p>
              </div>

              <div>
                <label htmlFor="minimum_order_amount" className="block text-sm font-medium text-primary mb-sm">
                  {t('discountCodes.minimumOrderAmount')}
                </label>
                <div className="flex items-center gap-sm">
                  <span className="text-sm font-medium text-secondary">{currency}</span>
                  <NumberInput
                    value={formData.minimum_order_amount ?? 0}
                    onChange={(v) => setFormData(prev => ({ ...prev, minimum_order_amount: v || null }))}
                    mode="decimal"
                    min={0}
                    step="0.01"
                    placeholder="No minimum"
                    className="input flex-1"
                  />
                </div>
                <p className="text-xs text-secondary mt-xs">{t('discountCodes.minOrderDesc')}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-sm">
                {t('discountCodes.applicableProducts')}
              </label>
              <div className="border border-primary rounded-lg p-md max-h-64 overflow-y-auto">
                {products.length === 0 ? (
                  <p className="text-secondary text-sm">{t('discountCodes.noProducts')}</p>
                ) : (
                  <div className="space-y-sm">
                    {products.map((product) => (
                      <label
                        key={product.id}
                        className="flex items-center gap-sm p-sm hover:bg-secondary rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.applicable_product_types.includes(product.id)}
                          onChange={() => handleProductToggle(product.id)}
                          className="w-4 h-4 text-blue border-gray-300 rounded focus:ring-blue"
                        />
                        <span className="text-sm text-primary">{product.product_name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-secondary mt-xs">
                {t('discountCodes.allProductsHint')}
                {formData.applicable_product_types.length > 0 && ` Currently applies to ${formData.applicable_product_types.length} selected product${formData.applicable_product_types.length !== 1 ? 's' : ''}.`}
              </p>
            </div>
          </div>

          {/* Section 3: Usage Statistics (Edit mode only) */}
          {isEditMode && (
            <div className="border-t border-primary pt-lg space-y-lg">
              <h3 className="text-lg font-semibold text-primary">{t('discountCodes.usageStatistics')}</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-lg">
                <div className="bg-secondary p-md rounded-lg">
                  <p className="text-xs text-secondary mb-xs">{t('discountCodes.totalUses')}</p>
                  <p className="text-2xl font-bold text-primary">{usageStats.current_uses}</p>
                  {formData.max_uses && (
                    <p className="text-xs text-secondary mt-xs">of {formData.max_uses} maximum</p>
                  )}
                </div>

                <div className="bg-secondary p-md rounded-lg">
                  <p className="text-xs text-secondary mb-xs">{t('discountCodes.totalValueUsed')}</p>
                  <p className="text-2xl font-bold text-primary">{currency} {usageStats.current_value_used.toFixed(2)}</p>
                  {formData.discount_type === 'timecard' && (
                    <p className="text-xs text-secondary mt-xs">
                      {t('discountCodes.remaining')} {currency} {(formData.discount_value - usageStats.current_value_used).toFixed(2)}
                    </p>
                  )}
                </div>

                <div className="bg-secondary p-md rounded-lg">
                  <p className="text-xs text-secondary mb-xs">{t('discountCodes.created')}</p>
                  <p className="text-sm font-medium text-primary">
                    {formatDate(usageStats.created_at)}
                  </p>
                  <p className="text-xs text-secondary mt-xs">
                    {formatTime(usageStats.created_at)}
                  </p>
                </div>

                <div className="bg-secondary p-md rounded-lg">
                  <p className="text-xs text-secondary mb-xs">{t('discountCodes.lastUpdated')}</p>
                  <p className="text-sm font-medium text-primary">
                    {formatDate(usageStats.updated_at)}
                  </p>
                  <p className="text-xs text-secondary mt-xs">
                    {formatTime(usageStats.updated_at)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons - Mobile Bottom */}
      <div className="lg:hidden flex items-center gap-md">
        {isEditMode && (
          <button onClick={handleDelete} className="btn btn-ghost text-red-600 hover:bg-red-50">
            <Trash2 className="w-4 h-4" />
            {t('common.delete')}
          </button>
        )}
        <button onClick={handleCancel} className="btn btn-ghost flex-1">
          <X className="w-4 h-4" />
          {t('common.cancel')}
        </button>
        <button onClick={handleSave} className="btn btn-primary flex-1" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('common.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {isEditMode ? t('common.update') : t('common.save')}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
